import React from "react";
import { observer } from "mobx-react-lite";
import {
  Navbar,
  Alignment,
  EditableText,
  Popover,
  Button,
  Menu,
  MenuItem,
  Position,
  NavbarDivider,
} from "@blueprintjs/core";

import MdcCloudAlert from "@meronex/icons/mdc/MdcCloudAlert";
import MdcCloudCheck from "@meronex/icons/mdc/MdcCloudCheck";
import MdcCloudSync from "@meronex/icons/mdc/MdcCloudSync";
import styled from "polotno/utils/styled";

import { useProject } from "../project";
import { FileMenu } from "./file-menu";
import { DownloadButton } from "./download-button";
import { CloudWarning } from "../cloud-warning";

import { getSession, clearSession } from "../auth";

const NavbarContainer = styled("div")`
  white-space: nowrap;

  @media screen and (max-width: 500px) {
    overflow-x: auto;
    overflow-y: hidden;
    max-width: 100vw;
  }
`;

const NavInner = styled("div")`
  @media screen and (max-width: 500px) {
    display: flex;
  }
`;

function getInitials(nameOrEmail) {
  const s = (nameOrEmail || "").trim();
  if (!s) return "??";

  if (s.includes("@")) {
    const u = s.split("@")[0].replace(/[^a-zA-Z0-9]/g, "");
    return (u.slice(0, 2) || "??").toUpperCase();
  }

  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const ProfileCircle = ({ text }) => (
  <Button
    minimal
    style={{
      width: 34,
      height: 34,
      minWidth: 34,
      borderRadius: 999,
      padding: 0,
      display: "grid",
      placeItems: "center",
      fontWeight: 800,
      letterSpacing: 0.5,
      border: "1px solid rgba(0,0,0,0.15)",
      background: "rgba(255,255,255,0.6)",
    }}
    aria-label="Profile menu"
  >
    {text}
  </Button>
);

const Status = observer(({ project }) => {
  const Icon = !project.cloudEnabled
    ? MdcCloudAlert
    : project.status === "saved"
    ? MdcCloudCheck
    : MdcCloudSync;

  return (
    <Popover
      content={
        <div style={{ padding: "10px", maxWidth: "300px" }}>
          {!project.cloudEnabled && <CloudWarning style={{ padding: "10px" }} />}
          {project.cloudEnabled && project.status === "saved" && (
            <>
              Your data is saved with{" "}
              <a href="https://puter.com" target="_blank" rel="noreferrer">
                Puter.com
              </a>
            </>
          )}
          {project.cloudEnabled &&
            (project.status === "saving" || project.status === "has-changes") &&
            "Saving..."}
        </div>
      }
      interactionKind="hover"
    >
      <div style={{ padding: "0 5px" }}>
        <Icon className="bp5-icon" style={{ fontSize: "25px", opacity: 0.8 }} />
      </div>
    </Popover>
  );
});


export default observer(({ store }) => {
  const project = useProject();

  const user = getSession();
  const initials = getInitials(user?.displayName || user?.username);

  const handleLogout = () => {
    clearSession();
    window.location.reload();
  };

  return (
    <NavbarContainer className="bp5-navbar">
      <NavInner>
        <Navbar.Group align={Alignment.LEFT}>
          <div
            style={{
              marginRight: "20px",
              maxWidth: "200px",
              fontWeight: 800,
              fontSize: "18px",
              whiteSpace: "nowrap",
            }}
          >
            mock-app
          </div>

          <FileMenu store={store} project={project} />

          <div style={{ paddingLeft: "20px", maxWidth: "200px" }}>
            <EditableText
              value={window.project.name}
              placeholder="Design name"
              onChange={(name) => {
                window.project.name = name;
                window.project.requestSave();
              }}
            />
          </div>
        </Navbar.Group>

        <Navbar.Group align={Alignment.RIGHT}>
          <Status project={project} />
          <DownloadButton store={store} />
          {user && (
            <>
              <NavbarDivider />

              <Popover
                position={Position.BOTTOM_RIGHT}
                minimal
                content={
                  <Menu>
                    <MenuItem disabled text={user.displayName || user.username} />
                    <MenuItem icon="log-out" text="Logout" onClick={handleLogout} />
                  </Menu>
                }
              >
                <ProfileCircle text={initials} />
              </Popover>
            </>
          )}
        </Navbar.Group>
      </NavInner>
    </NavbarContainer>
  );
});
